import { writable } from 'svelte/store';
import collect from 'collect.js';
// Models
import MultiExpressGroup from '@/Models/MultiExpressGroup.js';
import { uid } from 'uid';
import MultiExpressGroupParentMarket from '@/Models/MultiExpressGroupParentMarket.js';
import { notify } from '@/Services/Notify.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let meta;

	const { subscribe, set, update } = writable(collect());

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getMultiExpressGroups() {
			let multiExpressGroup = new MultiExpressGroup();

			return multiExpressGroup
				.with('parent_markets')
				.get()
				.then((result) => {
					if (result.meta) {
						meta = result.meta;
					}
					set(collect(result.data));
				});
		},

		addNewMultiExpressGroupToStore() {
			update((items) => {
				let multiExpressGroup = new MultiExpressGroup();
				multiExpressGroup.temp_id = uid(8); //so we can key the list of entries
				multiExpressGroup.new = new Date().valueOf();
				multiExpressGroup.order = items.count() > 0 ? items.max('order') + 1 : 1;
				items.push(multiExpressGroup);
				return items;
			});
		},

		addNewMultiExpressGroupParentMarketToStore(multiExpressGroupId, parentMarket) {
			let hasDuplicate;

			update((items) => {
				items.each((item) => {
					if (item.id === multiExpressGroupId) {
						item.parent_markets.data.each((i) => {
							if (i.parent_market.id == parentMarket.id) {
								hasDuplicate = true;
							}
						});

						if (!hasDuplicate) {
							let multiExpressGroupParentMarket = new MultiExpressGroupParentMarket();
							multiExpressGroupParentMarket.temp_id = uid(8); //so we can key the list of entries
							multiExpressGroupParentMarket.multi_express_group_id = multiExpressGroupId;
							multiExpressGroupParentMarket.parent_market = {
								id: parentMarket.id,
								name: parentMarket.name
							};
							multiExpressGroupParentMarket.order =
								item.parent_markets.data.count() > 0 ? item.parent_markets.data.max('order') + 1 : 1;
							item.parent_markets.data.push(multiExpressGroupParentMarket);
							return item;
						} else {
							notify({
								type: 'danger',
								title: 'Error!',
								message: 'Parent market already added in Multi Express Group',
								timeout: 5
							});
						}
					}
				});
				return items;
			});
		},

		updateMultiExpressGroup(multiExpressGroup) {
			const parentMarketPayload = [];
			if (multiExpressGroup.parent_markets) {
				let order = 1;
				for (const parentMarket of multiExpressGroup.parent_markets.data.sortBy('order').all()) {
					parentMarketPayload.push({
						id: parentMarket.parent_market.id,
						order: order
					});
					order++;
				}
			}

			const multiExpressGroupPayload = {
				competition_id: multiExpressGroup.competition_id,
				order: multiExpressGroup.order,
				parent_markets: parentMarketPayload
			};
			let result = !multiExpressGroup.id
				? multiExpressGroup.saveMultiExpressGroup(multiExpressGroupPayload)
				: multiExpressGroup.patch(multiExpressGroupPayload);
			return result.then((result) => {
				this.getMultiExpressGroups().then(() => {
					return result;
				});
			});
		},

		saveMultiExpressGroupOrder(multiExpressGroup) {
			return multiExpressGroup.patch({ order: multiExpressGroup.order });
		},

		deleteMultiExpressGroup(multiExpressGroup) {
			return multiExpressGroup.new
				? removeMultiExpressGroupFromStore(multiExpressGroup)
				: multiExpressGroup.delete().then((result) => {
						return removeMultiExpressGroupFromStore(multiExpressGroup).finally(() => result);
					});
		},

		deleteMultiExpressGroupParentMarket(parentMarket) {
			return removeMultiExpressGroupParentMarketFromStore(parentMarket);
		},

		changeMultiExpressGroupOrder(currentPosition, newPosition) {
			update((items) => {
				items = items.map((multiExpressGroup) => {
					if (multiExpressGroup.order === newPosition) {
						multiExpressGroup.order = currentPosition;
					} else if (multiExpressGroup.order === currentPosition) {
						multiExpressGroup.order = newPosition;
					}
					return multiExpressGroup;
				});
				return items;
			});
		},

		changeMultiExpressGroupParentMarketOrder(multiExpressGroupId, currentPosition, newPosition) {
			update((items) => {
				items.each((item) => {
					item.parent_markets.data.sortBy('order').each((multiExpressGroupParentMarket) => {
						if (multiExpressGroupParentMarket.multi_express_group_id === multiExpressGroupId) {
							if (multiExpressGroupParentMarket.order === newPosition) {
								multiExpressGroupParentMarket.order = currentPosition;
							} else if (multiExpressGroupParentMarket.order === currentPosition) {
								multiExpressGroupParentMarket.order = newPosition;
							}
							return multiExpressGroupParentMarket;
						}
						return item;
					});
				});
				return items;
			});
		}
	};

	function removeMultiExpressGroupFromStore(multiExpressGroup) {
		return new Promise((resolve) => {
			update((items) => {
				items = items.reject((item) => item.id === multiExpressGroup.id);
				return items;
			});
			resolve();
		});
	}

	function removeMultiExpressGroupParentMarketFromStore(parentMarket) {
		return new Promise((resolve) => {
			update((items) => {
				items.map((item) => {
					if (item.id == parentMarket.multi_express_group_id) {
						item.parent_markets.data = item.parent_markets.data.reject(
							(l) => l.parent_market && l.parent_market.id && l.parent_market.id === parentMarket.parent_market.id
						);
					}
					return item;
				});
				return items;
			});
			resolve();
		});
	}

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
